import { useCallback, useState } from 'react';
import {
  VALUE_CHAIN_MAP_CUSTOM_NODES,
  VALUE_CHAIN_MAP_MODALS,
} from 'constants/interfaces';

import useCreateConnection from './useCreateConnection';

import type { Connection, Edge } from '@xyflow/react';
import type IExpandedState from 'components/ConnectionVCModal/interfaces/IExpandedState';
import type { MouseEvent } from 'react';
import type { ICustomEdge } from '../interfaces/ICustomEdge';
import type { ICustomUnitNodeData } from '../interfaces/ICustomUnitNode';
import type { ISubmitConnection } from '../interfaces/ISubmitConnection';
import type { IUseValueChainConnectionsParams } from '../interfaces/IUseValueChainConnections';

const useValueChainConnections = ({
  edges,
  nodesRef,
  valueChainMapId,
  setActiveModal,
}: IUseValueChainConnectionsParams) => {
  const [currentConnection, setCurrentConnection] = useState<Connection | undefined>(undefined);
  const [selectedEdge, setSelectedEdge] = useState<Edge[] | undefined>(undefined);

  const {
    isCreateLogicalConnectionLoading,
    isUpdateLogicalConnectionLoading,
    isDeleteLogicalConnectionLoading,
    isCreateSupplierConnectionLoading,
    isUpdateSupplierConnectionLoading,
    isDeleteSupplierConnectionLoading,
    isCreateServiceConnectionLoading,
    isUpdateServiceConnectionLoading,
    isDeleteServiceConnectionLoading,
    isCreateManufactureConnectionLoading,
    isUpdateManufactureConnectionLoading,
    isDeleteManufactureConnectionLoading,
    onActionConnect,
  } = useCreateConnection({ selectedEdge: selectedEdge || [] });

  const onConnect = useCallback(
    (params: Connection) => {
      if (!params.source) {
        return;
      }

      const sourceNode = nodesRef.current.find((nodeItem) => nodeItem.id === params.source);

      if (sourceNode && sourceNode.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
        const sourceData = sourceNode.data as unknown as ICustomUnitNodeData;
        const isStackedCountryGroupLeader = Boolean(
          sourceData.isCountryGroupLeader && sourceData.isCountryGroupCollapsed,
        );

        if (isStackedCountryGroupLeader) {
          return;
        }
      }

      const existingEdges = edges.filter(
        (edge) => edge.target === params.target && edge.source === params.source,
      );

      if (existingEdges.length > 0) {
        setSelectedEdge(existingEdges);
        setCurrentConnection(undefined);
        setActiveModal(VALUE_CHAIN_MAP_MODALS.VALUE_CONNECTION);
        return;
      }

      setCurrentConnection(params);
      setSelectedEdge(undefined);
      setActiveModal(VALUE_CHAIN_MAP_MODALS.VALUE_CONNECTION);
    },
    [edges, nodesRef, setActiveModal],
  );

  const getGroupEdges = useCallback((edge: Edge): boolean => {
    const edgeData = edge.data as Partial<ICustomEdge['data']> | undefined;
    const groupSource = edgeData?.originalSource ?? edge.source;
    const groupTarget = edgeData?.originalTarget ?? edge.target;
    const groupEdges = edges.filter((edgeItem) => edgeItem.target === groupTarget
      && edgeItem.source === groupSource);

    if (!groupEdges.length) {
      return false;
    }

    setSelectedEdge(groupEdges);
    return true;
  }, [edges]);

  const onEdgeDoubleClick = useCallback((event: MouseEvent, edge: Edge) => {
    if (!getGroupEdges(edge)) {
      return;
    }

    setActiveModal(VALUE_CHAIN_MAP_MODALS.VALUE_CONNECTION);
  }, [getGroupEdges, setActiveModal]);

  const onEdgeClick = useCallback((event: MouseEvent, edge: Edge) => {
    getGroupEdges(edge);
  }, [getGroupEdges]);

  const onAddServiceConnection = (values: ISubmitConnection, connection: IExpandedState, action: string) => {
    onActionConnect(values, currentConnection, connection, valueChainMapId, action);
  };

  return {
    selectedEdge,
    setSelectedEdge,
    setCurrentConnection,
    onConnect,
    onEdgeClick,
    onEdgeDoubleClick,
    onAddServiceConnection,
    isCreateLogicalConnectionLoading,
    isUpdateLogicalConnectionLoading,
    isDeleteLogicalConnectionLoading,
    isCreateSupplierConnectionLoading,
    isUpdateSupplierConnectionLoading,
    isDeleteSupplierConnectionLoading,
    isCreateServiceConnectionLoading,
    isUpdateServiceConnectionLoading,
    isDeleteServiceConnectionLoading,
    isCreateManufactureConnectionLoading,
    isUpdateManufactureConnectionLoading,
    isDeleteManufactureConnectionLoading,
  };
};

export default useValueChainConnections;
